import React, { useState, useMemo } from 'react';
import { format, parseISO } from 'date-fns';
import { formatAppDateTime, isJourneyOverdue, journeyDateTimeValue } from '../utils/time';

const JourneyList = ({ journeys, onJourneyCompleted, onJourneyCancelled }) => {
  const [filter, setFilter] = useState('all'); // all, active, completed, cancelled
  const [sortBy, setSortBy] = useState('return_date'); // return_date, created_at, destination

  const formatDateTime = (date, time) => {
    try {
      const dateTime = parseISO(`${date}T${time}:00`);
      return format(dateTime, 'MMM dd, yyyy \'at\' HH:mm');
    } catch (error) {
      return `${date} at ${time}`;
    }
  };

  const filteredAndSortedJourneys = useMemo(() => {
    let filtered = journeys;

    // Apply filter
    if (filter !== 'all') {
      filtered = journeys.filter(journey => journey.status === filter);
    }

    // Apply sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'return_date':
          return journeyDateTimeValue(a.return_date, a.return_time)
            .localeCompare(journeyDateTimeValue(b.return_date, b.return_time));
        case 'created_at':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'destination':
          return a.destination.localeCompare(b.destination);
        default:
          return 0;
      }
    });

    return filtered;
  }, [journeys, filter, sortBy]);

  const getStatusBadge = (journey) => {
    const isOverdue = isJourneyOverdue(journey.return_date, journey.return_time);
    
    switch (journey.status) {
      case 'active':
        return isOverdue ? (
          <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded-full">
            🚨 OVERDUE
          </span>
        ) : (
          <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
            ✅ Active
          </span>
        );
      case 'completed':
        return (
          <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
            ✅ Completed
          </span>
        );
      case 'cancelled':
        return (
          <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">
            ❌ Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const getJourneyStats = () => {
    const active = journeys.filter(j => j.status === 'active').length;
    const completed = journeys.filter(j => j.status === 'completed').length;
    const cancelled = journeys.filter(j => j.status === 'cancelled').length;
    const overdue = journeys.filter(j => 
      j.status === 'active' && 
      isJourneyOverdue(j.return_date, j.return_time)
    ).length;

    return { active, completed, cancelled, overdue };
  };

  const stats = getJourneyStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Journey History</h1>
        <p className="mt-2 text-gray-600">
          View and manage all your hiking safety alerts
        </p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
          <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          <p className="text-sm text-green-700">Active</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-center">
          <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
          <p className="text-sm text-red-700">Overdue</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
          <p className="text-2xl font-bold text-blue-600">{stats.completed}</p>
          <p className="text-sm text-blue-700">Completed</p>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
          <p className="text-2xl font-bold text-gray-600">{stats.cancelled}</p>
          <p className="text-sm text-gray-700">Cancelled</p>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          {/* Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Filter:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">All Journeys ({journeys.length})</option>
              <option value="active">Active ({stats.active})</option>
              <option value="completed">Completed ({stats.completed})</option>
              <option value="cancelled">Cancelled ({stats.cancelled})</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Sort by:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="return_date">Return Date</option>
              <option value="created_at">Created Date</option>
              <option value="destination">Destination</option>
            </select>
          </div>
        </div>
      </div>

      {/* Journey List */}
      <div className="space-y-4">
        {filteredAndSortedJourneys.length > 0 ? (
          filteredAndSortedJourneys.map(journey => (
            <div key={journey.id} className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{journey.destination}</h3>
                  <p className="text-sm text-gray-600">Journey #{journey.id}</p>
                </div>
                {getStatusBadge(journey)}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Expected Return:</p>
                  <p className="text-sm text-gray-900">
                    {formatDateTime(journey.return_date, journey.return_time)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Emergency Contact:</p>
                  <p className="text-sm text-gray-900">{journey.contact_name}</p>
                  {journey.contact_phone && (
                    <p className="text-sm text-gray-600">{journey.contact_phone}</p>
                  )}
                </div>
              </div>

              {journey.additional_info && (
                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700">Additional Info:</p>
                  <p className="text-sm text-gray-900">{journey.additional_info}</p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Created: {formatAppDateTime(new Date(journey.created_at))}
                  {journey.notification_sent === 1 && (
                    <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                      🔔 Notified
                    </span>
                  )}
                </div>

                {journey.status === 'active' && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onJourneyCompleted(journey.id)}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors"
                    >
                      ✅ I'm Safe!
                    </button>
                    <button
                      onClick={() => onJourneyCancelled(journey.id)}
                      className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm border">
            <span className="text-6xl mb-4 block">🔍</span>
            <h3 className="text-xl font-medium text-gray-900 mb-2">
              No journeys found
            </h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? "You haven't registered any journeys yet."
                : `No ${filter} journeys found. Try adjusting your filter.`
              }
            </p>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="bg-gray-50 p-4 rounded-lg border">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Status Legend:</h3>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            <span>Active - Monitoring in progress</span>
          </div>
          <div className="flex items-center">
            <span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span>
            <span>Overdue - Emergency alert may be triggered</span>
          </div>
          <div className="flex items-center">
            <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
            <span>Completed - Returned safely</span>
          </div>
          <div className="flex items-center">
            <span className="w-3 h-3 bg-gray-500 rounded-full mr-2"></span>
            <span>Cancelled - Alert disabled</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JourneyList;
